/**
 * Пределы полей — зеркало серверных.
 *
 * Длины взяты из описания API сервера (airel-cs, `src/Airel.Api/openapi.json`):
 * там они попадают из `[StringLength]` и `[Length]` на запросах. Это
 * дублирование, и обычно оно запрещено: второе описание разошлось бы с первым.
 *
 * Здесь оно оправдано одним: отказ сервера приходит одной строкой на всю
 * форму, и человек, набравший имя канала в двести знаков, узнаёт об этом
 * после отправки и без указания на поле. Но оправдание держится, только пока
 * описания совпадают, — поэтому предел не пишется в схеме числом, а берётся
 * из таблицы ниже, и таблицу сверяет с описанием API
 * `scripts/check-form-rules.mjs` на каждом `npm run check`.
 *
 * Таблица лежит здесь, а не при схемах, именно из-за сторожа: схемы разъедутся
 * по формам, а зеркало серверных длин обязано остаться одним файлом — иначе
 * проверять станет нечего.
 */

/**
 * Пределы полей и то, откуда они взяты.
 *
 * `from` — схема запроса в описании API, `field` — имя поля в ней. По этой
 * паре сторож и находит `minLength`/`maxLength` для сверки.
 */
export const LIMITS = {
  displayName: { min: 1, max: 64, from: "UpdateProfileRequest", field: "display_name" },
  applicationName: { min: 2, max: 32, from: "CreateApplicationRequest", field: "name" },
  applicationDescription: { max: 400, from: "UpdateApplicationRequest", field: "description" },
  bio: { max: 2000, from: "UpdateProfileRequest", field: "bio" },
  pronouns: { max: 32, from: "UpdateProfileRequest", field: "pronouns" },
  statusText: { max: 128, from: "UpdateProfileRequest", field: "status_text" },

  password: { min: 8, max: 1024, from: "RegisterRequest", field: "password" },
  registerName: { min: 1, max: 64, from: "RegisterRequest", field: "display_name" },

  channelName: { min: 1, max: 128, from: "CreateChannelRequest", field: "name" },
  channelTopic: { max: 200, from: "CreateChannelRequest", field: "topic" },

  serverName: { min: 1, max: 128, from: "CreateServerRequest", field: "name" },
  banReason: { max: 512, from: "BanRequest", field: "reason" },
} as const;

/**
 * Предел размера вложения, в байтах.
 *
 * Не поле формы, поэтому и не в `LIMITS`: в описании API его нет, он живёт в
 * домене — `FileTag.MaxSize` в airel-cs (`src/Airel.Domain/Files/FileTag.cs`).
 * Сверяет его тот же сторож, отдельной проверкой.
 *
 * Повтор оправдан тем же, чем и у полей, только резче: без него человек ждёт,
 * пока полсотни мегабайт уедут на сервер, и лишь тогда узнаёт, что предел
 * двадцать пять. С ним отказ приходит мгновенно и до всякой отправки.
 */
export const ATTACHMENT_MAX = 25 * 1024 * 1024;

/**
 * Пределы, которых на сервере нет: они наши и строже.
 *
 * Сторож их не проверяет — проверять нечего, второго описания не существует.
 * Но и заводить их походя нельзя: клиент, отказывающий в том, что сервер бы
 * принял, — это отказ без причины, и причина должна быть записана рядом.
 */
export const OWN = {
  /**
   * Описание сервера — 500, хотя сервер берёт 2000.
   *
   * Оно показывается на экране вступления по коду, целиком и без сворачивания:
   * две тысячи знаков там — это простыня вместо приглашения. Ограничение
   * стояло в поле и в подписи под ним ещё до всякой схемы; здесь оно только
   * названо вслух.
   */
  serverAbout: 500,
} as const;
